import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { GlassCard } from '../../../../../components/glass';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface FunilProps {
  importados: number;
  conciliados: number;
  exportados: number;
  aguardandoIdentificacao: number;
}

interface EtapaProps {
  label: string;
  valor: number;
  pct: number; // largura da barra (% do importado)
  gradient: string;
  perda: string;
  perdaColor: string;
  delay: number;
}

const Etapa = ({ label, valor, pct, gradient, perda, perdaColor, delay }: EtapaProps) => (
  <Box>
    <Flex justify="space-between" align="baseline" mb={1.5}>
      <Text fontSize="13px" fontWeight="600" color="#0F172A">
        {label}
      </Text>
      <Text fontSize="12px" color="#64748B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {pct.toFixed(1).replace('.', ',')}%
      </Text>
    </Flex>
    <Box position="relative" h="38px" borderRadius="10px" bg="#EEF0F6" overflow="hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(pct, 2)}%` }}
        transition={{ duration: 0.55, delay, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '10px',
          background: gradient,
        }}
      />
      <Flex position="absolute" inset={0} align="center" px={3} pointerEvents="none">
        <Text
          fontSize="16px"
          fontWeight="700"
          color="white"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          textShadow="0 1px 2px rgba(0,0,0,0.15)"
        >
          {formatNumber(valor)}
        </Text>
      </Flex>
    </Box>
    <Text fontSize="11px" color={perdaColor} mt={1} fontWeight="500">
      {perda}
    </Text>
  </Box>
);

// Funil Elegível → Conciliado → Exportado (Domínio), na base EXPORTÁVEL
// (partida-dobrada deduplicada) — as 3 etapas usam a mesma base pra o funil
// fechar. Exportado = fechado na Domínio. Reusa os KPIs do dashboard.
export const FunilProcessamento = ({
  importados,
  conciliados,
  exportados,
  aguardandoIdentificacao,
}: FunilProps) => {
  const base = Math.max(1, importados);
  const pctConc = (conciliados / base) * 100;
  const pctExp = (exportados / base) * 100;
  const taxaExport = pctExp;

  return (
    <GlassCard intensity="medium" p={6} h="full">
      <Flex justify="space-between" align="flex-start" gap={3} mb={5}>
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            Funil de processamento
          </Text>
          <Text fontSize="15px" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            Elegível → Conciliado → Exportado (Domínio)
          </Text>
          <Text fontSize="10px" color="#94A3B8" mt={0.5}>
            base exportável · partida-dobrada
          </Text>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Text
            fontSize="22px"
            fontWeight="700"
            color="#0EA371"
            lineHeight="1"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {taxaExport.toFixed(1).replace('.', ',')}%
          </Text>
          <Text fontSize="10px" color="#94A3B8" mt={0.5}>
            exportado
          </Text>
        </Box>
      </Flex>

      <Flex direction="column" gap={4}>
        <Etapa
          label="Elegíveis"
          valor={importados}
          pct={100}
          gradient="linear-gradient(90deg, #6d5ef0, #7c5cff)"
          perda={`${formatNumber(aguardandoIdentificacao)} aguardando identificação`}
          perdaColor="#94A3B8"
          delay={0}
        />
        <Etapa
          label="Conciliados"
          valor={conciliados}
          pct={pctConc}
          gradient="linear-gradient(90deg, #3b82f6, #3b7ddd)"
          perda={`${formatNumber(Math.max(0, importados - conciliados))} não conciliados`}
          perdaColor="#E08A06"
          delay={0.08}
        />
        <Etapa
          label="Exportados · Domínio"
          valor={exportados}
          pct={pctExp}
          gradient="linear-gradient(90deg, #10b981, #1fa971)"
          perda={`${formatNumber(Math.max(0, conciliados - exportados))} não exportados`}
          perdaColor="#94A3B8"
          delay={0.16}
        />
      </Flex>
    </GlassCard>
  );
};
